import { useState, useEffect, useRef } from 'react';
import { X, Key, ExternalLink, Loader2, CheckCircle, Zap, CreditCard, Wallet, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useLibraryCard } from './LibraryCardContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GUMROAD_STORE_URL = 'https://keywebster.gumroad.com/l/mimet';
const PAYBIS_URL = 'https://paybis.com';

const WALLET_QR = {
  usdt: { label: 'Tether (ERC20)', coin: 'USDT', network: 'Ethereum', address: '0xd12788C5Eecd4c3049bB4066FcB209dE3d086552', image: 'https://customer-assets.emergentagent.com/job_5c865009-3b35-4a8a-9410-145f05e5b4ce/artifacts/bdvx0mwa_IMG_4650.jpeg' },
  usdc: { label: 'USDC (Base)', coin: 'USDC', network: 'Base', address: '0xd12788C5Eecd4c3049bB4066FcB209dE3d086552', image: 'https://customer-assets.emergentagent.com/job_5c865009-3b35-4a8a-9410-145f05e5b4ce/artifacts/7hmq6zxu_IMG_4649.jpeg' },
  btc: { label: 'Bitcoin', coin: 'BTC', network: 'Bitcoin', address: '', image: 'https://customer-assets.emergentagent.com/job_5c865009-3b35-4a8a-9410-145f05e5b4ce/artifacts/345jamvn_IMG_4355.jpeg' },
  eth: { label: 'Ethereum', coin: 'ETH', network: 'Ethereum', address: '', image: 'https://customer-assets.emergentagent.com/job_5c865009-3b35-4a8a-9410-145f05e5b4ce/artifacts/fwksfrpt_IMG_4357.jpeg' },
};

const CRYPTO_PACKAGES = [
  { tokens: 3, price: 20, label: '3 Tokens' },
  { tokens: 7, price: 50, label: '7 Tokens' },
  { tokens: 14, price: 100, label: '14 Tokens' },
];

const RedeemTokenModal = ({ open, onClose }) => {
  const { card, redeemGumroad, refreshCard } = useLibraryCard();
  const [activeTab, setActiveTab] = useState('gumroad');
  const [licenseKey, setLicenseKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [redeemed, setRedeemed] = useState(null);
  const [cryptoOrderId, setCryptoOrderId] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState('usdt');
  const [walletTxHash, setWalletTxHash] = useState('');
  const [walletTokenCount, setWalletTokenCount] = useState('1');
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  if (!open) return null;

  const handleRedeemGumroad = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) { toast.error('Enter your Gumroad license key'); return; }
    setSubmitting(true);
    try {
      const result = await redeemGumroad(licenseKey.trim());
      setRedeemed(result);
      toast.success(`${result.tokens_added} token(s) added!`);
      await refreshCard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Redemption failed');
    } finally { setSubmitting(false); }
  };

  const handleCreateCryptoPayment = async (pkg) => {
    if (!card?.id) { toast.error('Library card required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/library-card/create-crypto-payment`, { card_id: card.id, amount_usd: pkg.price });
      if (res.data.success && res.data.invoice_url) {
        setCryptoOrderId(res.data.order_id);
        window.open(res.data.invoice_url, '_blank');
        toast.success('Payment page opened — complete payment there');
        startPolling(res.data.order_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    } finally { setSubmitting(false); }
  };

  const startPolling = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setCheckingPayment(true);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/library-card/check-crypto-payment/${orderId}`);
        if (res.data.status === 'completed') {
          clearInterval(pollRef.current); pollRef.current = null; setCheckingPayment(false);
          setRedeemed({ tokens_added: res.data.tokens_credited, new_balance: res.data.new_balance });
          toast.success(`${res.data.tokens_credited} token(s) added!`);
          await refreshCard();
        } else if (['expired', 'failed', 'refunded'].includes(res.data.status)) {
          clearInterval(pollRef.current); pollRef.current = null; setCheckingPayment(false);
          toast.error(`Payment ${res.data.status}`);
        }
      } catch (e) { console.debug('Payment poll:', e?.message); }
    }, 10000);
  };

  const handleWalletSubmit = async (e) => {
    e.preventDefault();
    if (!walletTxHash.trim()) { toast.error('Enter your transaction hash'); return; }
    if (!card?.id) { toast.error('Library card required'); return; }
    const count = parseInt(walletTokenCount) || 1;
    const amountUsd = count * 5.50;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/library-card/submit-wallet-payment`, {
        card_id: card.id,
        tx_hash: walletTxHash.trim(),
        coin: WALLET_QR[selectedWallet].coin,
        token_count: count,
        amount_usd: amountUsd,
      });
      if (res.data.success) {
        setRedeemed({ tokens_added: 0, status: 'pending' });
        toast.success('Payment submitted! Tokens will be credited after verification.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="redeem-token-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-slate-950 border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-800/40 flex-shrink-0">
            <h2 className="text-lg font-bold text-white">Buy & Redeem Tokens</h2>
            <button onClick={onClose} className="text-slate-500 hover:text-white p-1 rounded-lg hover:bg-slate-800">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-slate-800/40 flex-shrink-0">
            <button onClick={() => { setActiveTab('gumroad'); setRedeemed(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === 'gumroad' ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/5' : 'text-slate-500 hover:text-slate-300'}`}
              data-testid="tab-gumroad">
              <CreditCard className="w-4 h-4" /> Gumroad
            </button>
            <button onClick={() => { setActiveTab('wallet'); setRedeemed(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === 'wallet' ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300'}`}
              data-testid="tab-wallet">
              <Wallet className="w-4 h-4" /> Direct Pay
            </button>
            <button onClick={() => { setActiveTab('crypto'); setRedeemed(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === 'crypto' ? 'text-amber-400 border-b-2 border-amber-400 bg-amber-500/5' : 'text-slate-500 hover:text-slate-300'}`}
              data-testid="tab-crypto">
              <Zap className="w-4 h-4" /> 200+ Coins
            </button>
          </div>

          {/* Content — scrollable */}
          <div className="overflow-y-auto flex-1 px-6 py-5">
            {redeemed ? (
              redeemed.status === 'pending' ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 flex items-center gap-3" data-testid="payment-pending">
                  <Loader2 className="w-8 h-8 text-amber-400 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-amber-300">Payment Submitted</p>
                    <p className="text-sm text-amber-400/70">Your tokens will be credited after we verify the transaction. This usually takes up to 24 hours.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 flex items-center gap-3" data-testid="redeem-success">
                  <CheckCircle className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-base font-medium text-emerald-300">+{redeemed.tokens_added} token{redeemed.tokens_added > 1 ? 's' : ''} added!</p>
                    <p className="text-sm text-emerald-400/70">New balance: {redeemed.new_balance} tokens</p>
                  </div>
                </div>
              )
            ) : activeTab === 'gumroad' ? (
              /* ── Gumroad ── */
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span className="text-xs bg-cyan-500/20 text-cyan-400 rounded-full w-5 h-5 flex items-center justify-center font-bold">1</span>
                    Buy tokens — $5/token
                  </h3>
                  <a href={GUMROAD_STORE_URL} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-900/60 border border-slate-700/50 hover:border-cyan-600/50 hover:bg-slate-800/60 transition-all group" data-testid="buy-gumroad">
                    <div>
                      <p className="text-sm font-semibold text-white">NextXus HumanCodex Stack</p>
                      <p className="text-xs text-slate-500">Best price — $5 per token</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors" />
                  </a>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span className="text-xs bg-cyan-500/20 text-cyan-400 rounded-full w-5 h-5 flex items-center justify-center font-bold">2</span>
                    Enter your license key
                  </h3>
                  <form onSubmit={handleRedeemGumroad} className="flex gap-2">
                    <div className="relative flex-1">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input type="text" placeholder="Paste Gumroad license key" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)}
                        className="pl-10 bg-slate-900/60 border-slate-700/60 focus:border-cyan-600" data-testid="license-key-input" />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-cyan-600 hover:bg-cyan-500 text-sm font-medium px-5" data-testid="redeem-button">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Redeem'}
                    </Button>
                  </form>
                </div>
              </div>
            ) : activeTab === 'wallet' ? (
              /* ── Direct Wallet (USDT/USDC) ── */
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 rounded-full w-5 h-5 flex items-center justify-center font-bold">1</span>
                    Send $5.50 per token — scan QR
                  </h3>
                  <p className="text-[11px] text-amber-400/70 -mt-1.5 mb-3 ml-7">Tokens credited after manual verification (up to 24h). For instant tokens, use Gumroad.</p>
                  {/* Coin selector */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {Object.entries(WALLET_QR).map(([key, w]) => (
                      <button key={key} onClick={() => setSelectedWallet(key)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${selectedWallet === key ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-slate-700 text-slate-400 hover:border-slate-600'}`}
                        data-testid={`wallet-${key}`}>
                        {w.coin}
                      </button>
                    ))}
                  </div>
                  {/* QR code */}
                  <div className="bg-white rounded-xl p-3 flex justify-center">
                    <img src={WALLET_QR[selectedWallet].image} alt={`${WALLET_QR[selectedWallet].coin} QR`} className="w-48 h-48 object-contain" />
                  </div>
                  <p className="text-center text-xs text-slate-500 mt-2">
                    Send <span className="text-emerald-400 font-bold">$5.50 {WALLET_QR[selectedWallet].coin}</span> per token via Paybis wallet
                  </p>
                  {WALLET_QR[selectedWallet].address && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(WALLET_QR[selectedWallet].address); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Address copied!'); }}
                      className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/50 hover:border-emerald-600/50 transition-all group text-left"
                      data-testid="copy-wallet-address"
                    >
                      <span className="text-[10px] text-slate-400 font-mono truncate flex-1">{WALLET_QR[selectedWallet].address}</span>
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> : <Copy className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 flex-shrink-0" />}
                    </button>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 rounded-full w-5 h-5 flex items-center justify-center font-bold">2</span>
                    Submit your transaction
                  </h3>
                  <form onSubmit={handleWalletSubmit} className="space-y-3">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input type="text" placeholder="Transaction hash / ID" value={walletTxHash} onChange={(e) => setWalletTxHash(e.target.value)}
                          className="bg-slate-900/60 border-slate-700/60 focus:border-emerald-600 text-xs" data-testid="wallet-tx-input" />
                      </div>
                      <div className="w-20">
                        <Input type="number" min="1" max="100" placeholder="Qty" value={walletTokenCount} onChange={(e) => setWalletTokenCount(e.target.value)}
                          className="bg-slate-900/60 border-slate-700/60 focus:border-emerald-600 text-xs text-center" data-testid="wallet-qty-input" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Total: <span className="text-white font-medium">${((parseInt(walletTokenCount) || 1) * 5.50).toFixed(2)} {WALLET_QR[selectedWallet].coin}</span></span>
                      <span>= <span className="text-emerald-400 font-medium">{parseInt(walletTokenCount) || 1} token{(parseInt(walletTokenCount) || 1) > 1 ? 's' : ''}</span></span>
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-500 text-sm font-medium" data-testid="wallet-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Payment'}
                    </Button>
                  </form>
                </div>

                <div className="bg-slate-900/40 rounded-lg p-2.5 border border-slate-800/40">
                  <p className="text-[11px] text-slate-500">
                    Need crypto? Buy instantly on <a href={PAYBIS_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Paybis.com</a>
                  </p>
                </div>
              </div>
            ) : (
              /* ── NOWPayments (200+ coins) ── */
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span className="text-xs bg-amber-500/20 text-amber-400 rounded-full w-5 h-5 flex items-center justify-center font-bold">1</span>
                    Pay with 200+ cryptocurrencies
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {CRYPTO_PACKAGES.map(pkg => (
                      <button key={pkg.price} onClick={() => handleCreateCryptoPayment(pkg)} disabled={submitting}
                        className="flex flex-col items-center px-3 py-4 rounded-lg bg-slate-900/60 border border-slate-700/50 hover:border-amber-500/50 hover:bg-amber-950/20 transition-all"
                        data-testid={`crypto-pkg-${pkg.tokens}`}>
                        <p className="text-lg font-bold text-white">${pkg.price}</p>
                        <p className="text-xs text-amber-400 font-medium">{pkg.label}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-600 mt-2">
                    BTC, ETH, DOGE & more. Min $20. Best value on <a href={GUMROAD_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Gumroad</a> at $5/token.
                  </p>
                </div>
                {checkingPayment && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-300">Waiting for payment...</p>
                      <p className="text-xs text-amber-400/70">Complete payment in the opened tab.</p>
                    </div>
                  </div>
                )}
                <div className="bg-slate-900/40 rounded-lg p-2.5 border border-slate-800/40">
                  <p className="text-[11px] text-slate-500">
                    Need crypto? <a href={PAYBIS_URL} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">Paybis.com</a> — buy fast
                  </p>
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-600 text-center mt-4">
              Each token unlocks one full document. Tokens never expire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemTokenModal;
